import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Busca produtos de uma coleção, filtra por metafield custom.show_price (valor > 0)
 * e ordena pelo valor desse metafield (em vez do preço normal).
 * Suporta paginação no lado do cliente.
 * @param {string} collectionHandle
 * @param {number} page - página atual (base 1)
 * @param {number} limit - itens por página
 * @param {string} sortKey - ignorado (mantido pela compatibilidade, mas não usado)
 * @param {boolean} reverse - se true, ordenação decrescente; false, crescente
 * @param {Object} clientFilters - filtros extraídos do formulário
 * @param {string} tag - tag opcional para filtrar produtos
 * @returns {Promise<{ products: Array, totalCount: number }>}
 */
async function fetchRecentProducts(
  collectionHandle,
  page = 1,
  limit = 20,
  sortKey = "PRICE",
  reverse = true,
  clientFilters = {},
  tag = null
) {
  const ITEMS_PER_FETCH = 100;

  const query = `
    query GetCollectionProducts(
      $handle: String!
      $first: Int!
      $after: String
    ) {
      collection(handle: $handle) {
        products(first: $first, after: $after) {
          edges {
            node {
              id
              title
              handle
              productType
              publishedAt
              tags
              availableForSale
              options {
                name
                values
              }
              featuredImage {
                url
                altText
                width
                height
              }
              variants(first: 2) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                }
              }
              metafield(namespace: "custom", key: "show_price") {
                value
              }
              metafieldUnidadeVenda: metafield(namespace: "custom", key: "unidade_de_venda") {
                value
              }
              metafieldUnidadeMedida: metafield(namespace: "custom", key: "unidade_medida_produto") {
                value
              }
              metafieldMarca: metafield(namespace: "my_fields", key: "marca") {
                value
              }
              metafieldCores: metafield(namespace: "custom", key: "cores_do_produto") {
                value
              }
              metafieldEspessura: metafield(namespace: "my_fields", key: "espessura_do_produto") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  let allProducts = [];
  let hasNextPage = true;
  let afterCursor = null;

  try {
    while (hasNextPage) {
      const response = await fetch('https://jular-madeiras.myshopify.com/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '60fbaa23a5dd4347abe1091266fc8b2b',
        },
        body: JSON.stringify({
          query,
          variables: {
            handle: collectionHandle,
            first: ITEMS_PER_FETCH,
            after: afterCursor,
          },
        }),
      });

      const { data, errors } = await response.json();
      if (errors) throw new Error(errors[0].message);

      const productsEdges = data?.collection?.products?.edges || [];
      const pageInfo = data?.collection?.products?.pageInfo;

      for (const edge of productsEdges) {
        allProducts.push(edge.node);
      }

      hasNextPage = pageInfo?.hasNextPage || false;
      afterCursor = pageInfo?.endCursor || null;
    }

    let filteredProducts = allProducts;

    // 1. show_price > 0 (obrigatório)
    filteredProducts = filteredProducts.filter(product => {
      const metafieldValue = product.metafield?.value;
      if (!metafieldValue) return false;
      const numericValue = parseFloat(metafieldValue);
      return !isNaN(numericValue) && numericValue > 0;
    });

    // 2. Disponibilidade (filter.v.availability)
    if (clientFilters.availability && clientFilters.availability.length) {
      const inStock = clientFilters.availability.includes('1');
      const outOfStock = clientFilters.availability.includes('0');
      filteredProducts = filteredProducts.filter(product => {
        const available =
          product.availableForSale === true ||
          product.variants.edges.some(edge => edge.node?.availableForSale === true);
        if (inStock && outOfStock) return true;
        if (inStock) return available === true;
        if (outOfStock) return available === false;
        return true;
      });
    }

    // 3. Tipo de produto (filter.p.product_type)
    if (clientFilters.productType && clientFilters.productType.length) {
      filteredProducts = filteredProducts.filter(product =>
        clientFilters.productType.includes(product.productType)
      );
    }

    // 4. Marca (filter.p.m.my_fields.marca)
    if (clientFilters.marca && clientFilters.marca.length) {
      filteredProducts = filteredProducts.filter(product => {
        const marca = product.metafieldMarca?.value || '';
        return clientFilters.marca.includes(marca);
      });
    }

    // 5. Cores (filter.p.m.custom.cores_do_produto)
    if (clientFilters.cores && clientFilters.cores.length) {
      filteredProducts = filteredProducts.filter(product => {
        const rawValue = product.metafieldCores?.value;
        if (!rawValue) return false;
        let coresArray = [];
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed)) {
            coresArray = parsed;
          } else if (parsed && typeof parsed === 'string') {
            coresArray = [parsed];
          } else {
            coresArray = [rawValue];
          }
        } catch (e) {
          coresArray = [rawValue];
        }
        return coresArray.some(cor => clientFilters.cores.includes(cor));
      });
    }

    // 6. Espessura (filter.p.m.my_fields.espessura_do_produto)
    if (clientFilters.espessura && clientFilters.espessura.length) {
      filteredProducts = filteredProducts.filter(product => {
        const rawValue = product.metafieldEspessura?.value;
        if (!rawValue) return false;
        let espessuraArray = [];
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed)) {
            espessuraArray = parsed;
          } else if (parsed && typeof parsed === 'string') {
            espessuraArray = [parsed];
          } else {
            espessuraArray = [rawValue];
          }
        } catch (e) {
          espessuraArray = [rawValue];
        }
        return espessuraArray.some(val => clientFilters.espessura.includes(val));
      });
    }

    // 7. Preço (filter.v.price.gte / lte) – aplicado sobre o metafield show_price
    if (clientFilters.priceMin !== undefined || clientFilters.priceMax !== undefined) {
      const min = parseFloat(clientFilters.priceMin) || -Infinity;
      const max = parseFloat(clientFilters.priceMax) || Infinity;
      filteredProducts = filteredProducts.filter(product => {
        const price = parseFloat(product.metafield?.value);
        return price >= min && price <= max;
      });
    }

    // 8. Filtro por tag (se fornecida)
    if (tag) {
      filteredProducts = filteredProducts.filter(product =>
        product.tags && product.tags.includes(tag)
      );
      console.log(`Produtos após filtro por tag "${tag}":`, filteredProducts.length);
    }

    // Ordena pelo metafield show_price
    filteredProducts.sort((a, b) => {
      const valA = parseFloat(a.metafield.value);
      const valB = parseFloat(b.metafield.value);
      if (reverse) return valB - valA;
      else return valA - valB;
    });

    const totalCount = filteredProducts.length;
    const start = (page - 1) * limit;
    const paginatedProducts = filteredProducts.slice(start, start + limit);

    return { products: paginatedProducts, totalCount };
  } catch (error) {
    console.error('Erro ao buscar produtos:', error);
    return { products: [], totalCount: 0 };
  }
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  // Flag para ordenação por preço
  #priceSortFilter = false;
  // Página atual (client‑side)
  #currentPage = 1;

  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  #updateURLHash() {
    const notAcceptedFilters = ['price-ascending', 'price-descending'];
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    this.#priceSortFilter = false;

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      if (notAcceptedFilters.includes(value)) {
        this.#priceSortFilter = value;
      } else {
        url.searchParams.append(param, value);
      }
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    if (this.#priceSortFilter) {
      this.#currentPage = 1;          // reset ao mudar ordenação
      this.#loadAndRenderWithPriceSort();
      return;
    }

    this.#updateSection();
  };

  #updateSection() {
    const viewTransition = !this.closest('dialog');
    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }

  #getNumericId(id) {
    if (!id) return '';
    const match = String(id).match(/(\d+)$/);
    return match ? match[1] : String(id);
  }

  #getCartIconSvg() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="var(--icon-stroke-width)"
          d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"
        ></path>
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-width="var(--icon-stroke-width)"
          d="M14.608 12.563v5m2.5-2.5h-5"
        ></path>
      </svg>
    `;
  }

  #getAddedIconSvg() {
    return `
      <svg aria-hidden="true" class="checkmark-burst" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g class="check">
          <circle class="ring" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></circle>
          <path class="tick" d="M9 12.75L11.25 15L15 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
        </g>
        <g class="burst">
          <g style="--index: 0;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 1;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 2;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 3;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 4;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 5;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 6;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
          <g style="--index: 7;">
            <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"></line>
          </g>
        </g>
      </svg>
    `;
  }

  #buildQuickAdd(product, productUrl) {
    const variantEdges = product.variants?.edges || [];
    const variantNode = variantEdges[0]?.node;
    const hasOnlyOneVariant =
      variantEdges.length === 1 && product.variants?.pageInfo?.hasNextPage !== true;

    const productId = this.#getNumericId(product.id);
    const variantId = this.#getNumericId(variantNode?.id);

    const productAvailable =
      product.availableForSale === true ||
      variantEdges.some(edge => edge.node?.availableForSale === true);

    const canAddToCart =
      hasOnlyOneVariant &&
      variantNode?.availableForSale === true &&
      variantId !== '';

    const quickAddButton = hasOnlyOneVariant ? 'add' : 'choose';
    const optionsCount = product.options?.length || 0;
    const formId = `QuickAdd-ProductForm-${productId}-${this.sectionId}__card-gallery`;

    const iconSvg = this.#getCartIconSvg();
    const addedIconSvg = this.#getAddedIconSvg();

    const addText = canAddToCart ? 'Adicionar' : 'Esgotado';
    const chooseText = productAvailable ? 'Escolher' : 'Esgotado';

    const variantMedia = product.featuredImage?.url
      ? `${product.featuredImage.url}&width=100`
      : '';

    return `
      <quick-add-component
        class="quick-add color-scheme-5"
        ref="quickAdd"
        data-product-title="${this.#escapeHtml(product.title)}"
        data-quick-add-button="${quickAddButton}"
        data-product-options-count="${optionsCount}"
      >
        <product-form-component
          data-section-id="${this.sectionId}"
          data-product-id="${productId}"
          on:submit="/handleSubmit"
          class="quick-add__product-form-component"
        >
          <div
            class="visually-hidden"
            aria-live="assertive"
            role="status"
            aria-atomic="true"
            ref="liveRegion"
          ></div>

          <form
            method="post"
            action="/cart/add"
            id="${formId}"
            accept-charset="UTF-8"
            class="shopify-product-form js-quick-add-form"
            enctype="multipart/form-data"
            novalidate="novalidate"
            data-type="add-to-cart-form"
          >
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">

            <input
              type="hidden"
              name="id"
              ref="variantId"
              value="${variantId}"
              ${canAddToCart ? '' : 'disabled'}
            >

            <input type="hidden" name="quantity" value="1">

            <add-to-cart-component
              ref="addToCartButtonContainer"
              data-product-variant-media="${this.#escapeHtml(variantMedia)}"
              data-add-to-cart-animation="true"
            >
              <button
                type="submit"
                name="add"
                ref="addToCartButton"
                on:click="/handleClick"
                class="button button quick-add__button quick-add__button--add add-to-cart-button"
                data-quick-add-submit
                data-default-text="${this.#escapeHtml(addText)}"
                ${canAddToCart ? '' : 'disabled'}
              >
                <span class="add-to-cart-text">
                  <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">
                    ${iconSvg}
                  </span>

                  <span class="add-to-cart-text__content is-visually-hidden-mobile">
                    <span>
                      <span>${this.#escapeHtml(addText)}</span>
                    </span>
                  </span>
                </span>

                <span class="add-to-cart__added">
                  <span class="svg-wrapper add-to-cart__added-icon">
                    ${addedIconSvg}
                  </span>
                </span>
              </button>
            </add-to-cart-component>

            ${
              productAvailable
                ? `
                  <a
                    href="${productUrl}"
                    class="button quick-add__button quick-add__button--choose add-to-cart-button"
                    role="button"
                  >
                    <span class="add-to-cart-text">
                      <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">
                        ${iconSvg}
                      </span>

                      <span class="add-to-cart-text__content is-visually-hidden-mobile">
                        <span>
                          <span>${this.#escapeHtml(chooseText)}</span>
                        </span>
                      </span>
                    </span>
                  </a>
                `
                : `
                  <button
                    class="button quick-add__button quick-add__button--choose add-to-cart-button"
                    type="button"
                    name="add"
                    disabled
                  >
                    <span class="add-to-cart-text">
                      <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">
                        ${iconSvg}
                      </span>

                      <span class="add-to-cart-text__content is-visually-hidden-mobile">
                        <span>
                          <span>${this.#escapeHtml(chooseText)}</span>
                        </span>
                      </span>
                    </span>
                  </button>
                `
            }

            <input type="hidden" name="product-id" value="${productId}">
            <input type="hidden" name="section-id" value="${this.sectionId}">
          </form>
        </product-form-component>
      </quick-add-component>
    `;
  }

  #buildProductBadges(product, availableForSale, variantPriceNumeric, compareAtPriceNumeric) {
    const isSoldOut = availableForSale === false;
    const isOnSale =
      !isSoldOut &&
      compareAtPriceNumeric > variantPriceNumeric &&
      compareAtPriceNumeric > 0;

    if (!isSoldOut && !isOnSale) return '';

    let discountPercentage = 0;

    if (isOnSale) {
      const discountAmount = compareAtPriceNumeric - variantPriceNumeric;
      discountPercentage = Math.round((discountAmount * 100) / compareAtPriceNumeric);
    }

    return `
      <div
        class="product-badges product-badges--top-left"
        style="
          top: 8px;
          left: 8px;
          --badge-border-radius: 0px;
          --badge-font-family: var(--font-accent--family);
          --badge-font-weight: var(--font-accent--weight);
          --badge-text-transform: uppercase;
        "
      >
        <div class="product-badges__stack">
          <div
            class="
              product-badges__badge product-badges__badge--rectangle
              color-scheme-5
            "
          >
            ${isSoldOut ? 'Esgotado' : 'Promoção'}
          </div>

          ${
            isOnSale && discountPercentage > 0
              ? `
                <div class="product-badges__badge product-badges__badge--rectangle product-badges__badge--discount">
                  -${discountPercentage}%
                </div>
              `
              : ''
          }
        </div>
      </div>
    `;
  }

  // Geração do card (com lógica de preço do metafield e preço comparado proporcional)
  #buildProductCard(product) {
    const productUrl = `/products/${product.handle}`;
    const featuredImage = product.featuredImage;
    const imageUrl = featuredImage?.url || '';
    const imageAlt = featuredImage?.altText || product.title;

    const variantNode = product.variants.edges[0]?.node;
    const variantPrice = variantNode?.price;
    const compareAtPrice = variantNode?.compareAtPrice;
    const currencyCode = variantPrice?.currencyCode || 'EUR';
    const availableForSale =
      product.availableForSale === true ||
      product.variants.edges.some(edge => edge.node?.availableForSale === true);

    // --- Preço base (metafield custom.show_price) ---
    let currentPriceAmount = null;
    if (product.metafield?.value) {
      const numericValue = parseFloat(product.metafield.value);
      if (!isNaN(numericValue)) currentPriceAmount = numericValue;
    }
    if (currentPriceAmount === null) {
      currentPriceAmount = parseFloat(variantPrice?.amount || 0);
    }

    // --- Preço comparado (proporcional ao compare_at_price) ---
    let comparePriceAmount = null;
    const variantPriceNumeric = parseFloat(variantPrice?.amount || 0);
    const compareAtPriceNumeric = compareAtPrice?.amount ? parseFloat(compareAtPrice.amount) : 0;

    if (compareAtPriceNumeric > variantPriceNumeric && variantPriceNumeric > 0 && currentPriceAmount > 0) {
      comparePriceAmount = (currentPriceAmount * compareAtPriceNumeric) / variantPriceNumeric;
    }

    const badgesHtml = this.#buildProductBadges(
      product,
      availableForSale,
      variantPriceNumeric,
      compareAtPriceNumeric
    );

    // --- Unidade de venda (label) ---
    let unidadeRaw = product.metafieldUnidadeVenda?.value || '';
    let unidadeMedida = product.metafieldUnidadeMedida?.value || '';
    if (typeof unidadeRaw !== 'string') unidadeRaw = '';
    if (typeof unidadeMedida !== 'string') unidadeMedida = '';

    let displayUnit = unidadeMedida !== '' ? unidadeMedida : unidadeRaw;
    displayUnit = displayUnit.toLowerCase();

    let unidadeLabel = displayUnit;
    if (displayUnit === 'm2' || displayUnit === 'm²') {
      unidadeLabel = 'm²';
    } else if (displayUnit === 'ml') {
      unidadeLabel = 'mL';
    } else if (displayUnit === 'un' || displayUnit === 'unidade') {
      unidadeLabel = 'un';
    } else {
      unidadeLabel = displayUnit;
    }

    const showUnit = unidadeLabel !== '';
    const displayCurrent = currentPriceAmount.toFixed(2);
    const displayCompare = comparePriceAmount ? comparePriceAmount.toFixed(2) : null;
    const suffix = showUnit ? `/${unidadeLabel} + IVA` : '';

    // Valores em centavos (para os atributos data-*)
    const unitPriceCents = Math.round(currentPriceAmount * 100);
    const comparePriceCents = comparePriceAmount ? Math.round(comparePriceAmount * 100) : null;

    // Montagem do bloco de preço com a estrutura solicitada
    const priceHtml = `
      <div style="--color: var(--color-foreground-heading);" class="h5 price-per-unit-main${comparePriceCents ? ' price-per-unit-main--on-sale' : ''}" 
          data-unit-price-cents="${unitPriceCents}" 
          data-unit-label="${this.#escapeHtml(unidadeLabel)}">
        <span class="price-per-unit-main__current" data-unit-price-cents="${unitPriceCents}">
          ${this.#formatPrice(displayCurrent, currencyCode)}${suffix}
        </span>
        ${comparePriceCents ? `
          <span class="price-per-unit-main__compare">
            ${this.#formatPrice(displayCompare, currencyCode)}${suffix}
          </span>
        ` : ''}
      </div>
    `;

    const quickAddHtml = this.#buildQuickAdd(product, productUrl);

    // HTML completo do card
    return `
      <product-card-link
        data-product-id="${product.id}"
        data-featured-media-url="${imageUrl}"
        data-product-transition="false"
      >
        <div class="resource-card" data-resource-type="product">
          <a class="resource-card__link" href="${productUrl}">
            <span class="visually-hidden">${this.#escapeHtml(product.title)}</span>
          </a>

          <div class="resource-card__media" style="--resource-card-aspect-ratio: 1.0;">
            ${imageUrl ? `
              <img
                class="resource-card__image"
                src="${imageUrl}"
                alt="${this.#escapeHtml(imageAlt)}"
                loading="lazy"
                width="${featuredImage.width || 300}"
                height="${featuredImage.height || 300}"
              >
            ` : `
              <div class="resource-card__image-placeholder">
                ${this.#escapeHtml(product.title)}
              </div>
            `}

            ${badgesHtml}
            ${quickAddHtml}
          </div>

          <div class="resource-card__content">
            <p class="resource-card__title paragraph h5">
              ${this.#escapeHtml(product.title)}
            </p>
            ${priceHtml}
            ${!availableForSale ? '<span class="badge badge--sold-out">Esgotado</span>' : ''}
          </div>
        </div>
      </product-card-link>
    `;
  }

  #stopQuickAddClickHandler = (event) => {
    event.stopPropagation();
  };

  #quickAddSubmitHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    const button = form.querySelector('[data-quick-add-submit]');
    const variantInput = form.querySelector('input[name="id"]');
    const liveRegion = form
      .closest('product-form-component')
      ?.querySelector('[ref="liveRegion"]');

    if (!button || !variantInput || variantInput.disabled || !variantInput.value) return;

    const originalText = button.dataset.defaultText || 'Adicionar';

    const setButtonText = (text) => {
      const label = button.querySelector('.add-to-cart-text__content span span');
      if (label) label.textContent = text;
      if (liveRegion) liveRegion.textContent = text;
    };

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Não foi possível adicionar o produto.');
      }

      button.dataset.added = 'true';
      setButtonText('Adicionado');

      const cart = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      })
        .then(res => res.json())
        .catch(() => null);

      const detail = {
        cart,
        item: payload,
      };

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));

      setTimeout(() => {
        button.dataset.added = 'false';
        setButtonText(originalText);
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1500);
    } catch (error) {
      console.error(error);
      setButtonText('Erro');

      setTimeout(() => {
        setButtonText(originalText);
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1800);
    }
  };

  #bindQuickAddHandlers(container) {
    const forms = container.querySelectorAll('.js-quick-add-form');
    forms.forEach(form => {
      form.removeEventListener('submit', this.#quickAddSubmitHandler);
      form.addEventListener('submit', this.#quickAddSubmitHandler);
    });

    const buttons = container.querySelectorAll('.quick-add__button');
    buttons.forEach(button => {
      button.removeEventListener('click', this.#stopQuickAddClickHandler);
      button.addEventListener('click', this.#stopQuickAddClickHandler);
    });
  }

  #escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, (m) => {
      if (m === '&') return '&amp;';
      if (m === '<') return '&lt;';
      if (m === '>') return '&gt;';
      if (m === '"') return '&quot;';
      if (m === "'") return '&#039;';
      return m;
    });
  }

  #formatPrice(amount, currencyCode) {
    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    });
    return formatter.format(amount);
  }

  /**
   * Carrega e renderiza produtos usando ordenação por preço (price-ascending/descending)
   * e respeitando o filtro por metafield custom.show_price.
   * @param {number} [page] - Página a carregar (opcional)
   */
  async #loadAndRenderWithPriceSort(page) {
    if (typeof page === 'number' && page >= 1) {
      this.#currentPage = page;
    }

    // Extrai handle e tag da URL
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    let collectionHandle = null;
    let tag = null;
    if (pathSegments[0] === 'collections' && pathSegments.length >= 2) {
      collectionHandle = pathSegments[1];
      if (pathSegments.length >= 3) {
        tag = pathSegments[2];
      }
    }

    if (!collectionHandle) {
      console.warn('Handle de coleção não encontrado.');
      return;
    }

    let reverse = true;
    if (this.#priceSortFilter === 'price-ascending') {
      reverse = false;
    } else if (this.#priceSortFilter !== 'price-descending') {
      console.warn('Ordenação inesperada:', this.#priceSortFilter);
      return;
    }

    // Extrai os filtros do formulário
    const formData = new FormData(this.refs.facetsForm);
    const clientFilters = {
      availability: formData.getAll('filter.v.availability'),
      productType: formData.getAll('filter.p.product_type'),
      marca: formData.getAll('filter.p.m.my_fields.marca'),
      cores: formData.getAll('filter.p.m.custom.cores_do_produto'),
      espessura: formData.getAll('filter.p.m.my_fields.espessura_do_produto'),
      priceMin: formData.get('filter.v.price.gte') || '',
      priceMax: formData.get('filter.v.price.lte') || ''
    };

    // Converte os valores de preço (podem vir com vírgula e ponto)
    if (clientFilters.priceMin) {
      clientFilters.priceMin = parseFloat(clientFilters.priceMin.replace(',', '.'));
    }
    if (clientFilters.priceMax) {
      clientFilters.priceMax = parseFloat(clientFilters.priceMax.replace(',', '.'));
    }

    console.log('clientFilters:', clientFilters);
    console.log('FormData:', Array.from(formData.entries()));
    console.log('Tag extraída:', tag);

    const { products, totalCount } = await fetchRecentProducts(
      collectionHandle,
      this.#currentPage,
      20,
      "PRICE",
      reverse,
      clientFilters,
      tag
    );

    const productsContainer = document.querySelector('.product-grid');
    if (!productsContainer) return;

    if (products.length === 0) {
      productsContainer.innerHTML = '<p>Nenhum produto encontrado.</p>';
      this.#renderPagination(0);
      return;
    }

    const cardsHtml = products.map(p => this.#buildProductCard(p)).join('');
    productsContainer.innerHTML = cardsHtml;

    this.#bindQuickAddHandlers(productsContainer);

    const totalPages = Math.ceil(totalCount / 20);
    this.#renderPagination(totalPages);
  }

  /**
   * Renderiza controles de paginação (client‑side, sem alterar a URL).
   * @param {number} totalPages - Número total de páginas
   */
  #renderPagination(totalPages) {
    const paginationContainer = document.querySelector('.pagination');
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      return;
    }

    // Função auxiliar para construir a lista de páginas a mostrar
    const getPageRange = (current, total, delta = 2) => {
      const range = [];
      const rangeWithDots = [];
      let l;

      for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
          range.push(i);
        }
      }

      range.forEach((i) => {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      });
      return rangeWithDots;
    };

    const pages = getPageRange(this.#currentPage, totalPages);
    const currentPath = window.location.pathname; // apenas para aria-label, não usado como href

    // Monta o HTML da paginação
    let html = `
      <nav class="pagination" aria-label="Navegação por paginação" data-current_page="${this.#currentPage}">
        <ul class="pagination__list" role="list">
    `;

    // Botão "Anterior" (seta esquerda)
    if (this.#currentPage > 1) {
      html += `
        <li class="pagination__item">
          <button class="pagination__link pagination__link--arrow" data-page="${this.#currentPage - 1}" aria-label="Anterior">
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
        </li>
      `;
    } else {
      html += `
        <li class="pagination__item">
          <span class="pagination__link pagination__link--arrow disabled" aria-disabled="true">
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </span>
        </li>
      `;
    }

    // Botões de página e elipses
    for (const page of pages) {
      if (page === '...') {
        html += `
          <li class="pagination__item pagination__item--gap">
            <span class="pagination__link pagination__link--gap" aria-hidden="true">…</span>
          </li>
        `;
      } else if (page === this.#currentPage) {
        html += `
          <li class="pagination__item">
            <span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${page}</span>
          </li>
        `;
      } else {
        html += `
          <li class="pagination__item">
            <button class="pagination__link pagination__link--page" data-page="${page}" aria-label="Página ${page}">${page}</button>
          </li>
        `;
      }
    }

    // Botão "Seguinte" (seta direita)
    if (this.#currentPage < totalPages) {
      html += `
        <li class="pagination__item">
          <button class="pagination__link pagination__link--arrow" data-page="${this.#currentPage + 1}" aria-label="Seguinte">
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
        </li>
      `;
    } else {
      html += `
        <li class="pagination__item">
          <span class="pagination__link pagination__link--arrow disabled" aria-disabled="true">
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </span>
        </li>
      `;
    }

    html += `
        </ul>
      </nav>
    `;

    paginationContainer.innerHTML = html;

    // Reatribui os eventos a todos os botões com data-page
    const buttons = paginationContainer.querySelectorAll('button[data-page]');
    buttons.forEach(btn => {
      btn.removeEventListener('click', this.#paginationClickHandler);
      btn.addEventListener('click', this.#paginationClickHandler.bind(this));
    });
  }

  /**
   * Handler para clique na paginação.
   */
  #paginationClickHandler(event) {
    const btn = event.currentTarget;
    const newPage = parseInt(btn.dataset.page, 10);
    if (isNaN(newPage) || newPage === this.#currentPage) return;
    this.#loadAndRenderWithPriceSort(newPage);
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}